import { apiService } from './api';
import { 
  SEOScore, SEOIssue, PageSEOAnalysis, TrackedKeyword, 
  SuggestedKeyword, TechnicalSEO, SEOReport, KeywordAnalysis 
} from '../types/seo';

// Suggested keywords for car rental business
const CAR_RENTAL_KEYWORDS: SuggestedKeyword[] = [
  // Primary
  { keyword: 'car rental Perth', category: 'primary', searchVolume: 'high', difficulty: 'hard' },
  { keyword: 'hire car Perth', category: 'primary', searchVolume: 'high', difficulty: 'hard' },
  { keyword: 'rent a car Perth', category: 'primary', searchVolume: 'medium', difficulty: 'medium' },
  // Long-tail
  { keyword: 'cheap car rental Perth airport', category: 'long-tail', searchVolume: 'medium', difficulty: 'easy' },
  { keyword: 'weekly car hire Perth', category: 'long-tail', searchVolume: 'medium', difficulty: 'easy' },
  { keyword: 'monthly car rental Perth', category: 'long-tail', searchVolume: 'low', difficulty: 'easy' },
  { keyword: 'car rental Perth no deposit', category: 'long-tail', searchVolume: 'low', difficulty: 'easy' },
  // Local
  { keyword: 'car rental near me Perth', category: 'local', searchVolume: 'medium', difficulty: 'medium' },
  { keyword: 'Perth CBD car hire', category: 'local', searchVolume: 'low', difficulty: 'easy' },
  { keyword: 'car rental Fremantle', category: 'local', searchVolume: 'low', difficulty: 'easy' },
  // Service
  { keyword: 'SUV rental Perth', category: 'service', searchVolume: 'medium', difficulty: 'medium' },
  { keyword: 'sedan hire Perth', category: 'service', searchVolume: 'low', difficulty: 'easy' },
  { keyword: 'hatchback rental Perth', category: 'service', searchVolume: 'low', difficulty: 'easy' },
  { keyword: 'luxury car hire Perth', category: 'service', searchVolume: 'low', difficulty: 'medium' },
  // Comparison
  { keyword: 'best car rental Perth', category: 'comparison', searchVolume: 'medium', difficulty: 'medium' },
  { keyword: 'affordable car hire Perth', category: 'comparison', searchVolume: 'medium', difficulty: 'easy' },
  { keyword: 'cheapest car rental Perth', category: 'comparison', searchVolume: 'medium', difficulty: 'medium' },
];

class SEOService {
  private storageKey = 'tak8_seo_data';
  
  // Get stored SEO data
  private getStoredData(): any {
    const data = localStorage.getItem(this.storageKey);
    return data ? JSON.parse(data) : { keywords: [], scoreHistory: [], lastScan: null };
  }
  
  private saveData(data: any): void {
    localStorage.setItem(this.storageKey, JSON.stringify(data));
  }

  // Analyze text content
  analyzeContent(text: string): { wordCount: number; readabilityScore: number; readabilityGrade: string; sentences: number; avgWordsPerSentence: number } {
    const words = text.trim().split(/\s+/).filter(w => w.length > 0);
    const wordCount = words.length;
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0).length || 1;
    const avgWordsPerSentence = wordCount / sentences;
    
    // Simplified Flesch-Kincaid readability
    const syllables = words.reduce((count, word) => count + this.countSyllables(word), 0);
    const avgSyllablesPerWord = syllables / (wordCount || 1);
    const readabilityScore = Math.max(0, Math.min(100, 206.835 - (1.015 * avgWordsPerSentence) - (84.6 * avgSyllablesPerWord)));
    
    let readabilityGrade = 'Very Difficult';
    if (readabilityScore >= 90) readabilityGrade = 'Very Easy (5th grade)';
    else if (readabilityScore >= 80) readabilityGrade = 'Easy (6th grade)';
    else if (readabilityScore >= 70) readabilityGrade = 'Fairly Easy (7th grade)';
    else if (readabilityScore >= 60) readabilityGrade = 'Standard (8th-9th grade)';
    else if (readabilityScore >= 50) readabilityGrade = 'Fairly Difficult (10th-12th)';
    else if (readabilityScore >= 30) readabilityGrade = 'Difficult (College)';
    
    return { wordCount, readabilityScore: Math.round(readabilityScore), readabilityGrade, sentences, avgWordsPerSentence: Math.round(avgWordsPerSentence * 10) / 10 };
  }
  
  private countSyllables(word: string): number {
    word = word.toLowerCase().replace(/[^a-z]/g, '');
    if (word.length <= 3) return 1;
    const vowels = word.match(/[aeiouy]+/g);
    let count = vowels ? vowels.length : 1;
    if (word.endsWith('e')) count--;
    if (word.endsWith('le') && word.length > 2 && !/[aeiouy]/.test(word[word.length - 3])) count++;
    return Math.max(1, count);
  }

  // Calculate keyword density
  calculateKeywordDensity(text: string, keyword: string): { density: number; occurrences: number } {
    const words = text.toLowerCase().split(/\s+/);
    const keywordLower = keyword.toLowerCase();
    const keywordWords = keywordLower.split(/\s+/);
    
    let occurrences = 0;
    if (keywordWords.length === 1) {
      occurrences = words.filter(w => w.includes(keywordLower)).length;
    } else {
      const textLower = text.toLowerCase();
      let pos = 0;
      while ((pos = textLower.indexOf(keywordLower, pos)) !== -1) {
        occurrences++;
        pos += keywordLower.length;
      }
    }
    
    const density = words.length > 0 ? (occurrences / words.length) * 100 : 0;
    return { density: Math.round(density * 100) / 100, occurrences };
  }

  // Analyze a page's SEO
  async analyzePage(pageData: { url: string; title: string; metaTitle?: string; metaDescription?: string; content: string; h1?: string; h2s?: string[]; images?: { src: string; alt?: string }[] }): Promise<PageSEOAnalysis> {
    const { url, title, metaTitle, metaDescription, content, h1, h2s = [], images = [] } = pageData;
    const contentAnalysis = this.analyzeContent(content);
    
    // Meta title analysis
    const titleValue = metaTitle || title || '';
    const titleLength = titleValue.length;
    let titleStatus: 'pass' | 'warning' | 'fail' = 'pass';
    let titleMessage = 'Good length and format';
    if (!titleValue) { titleStatus = 'fail'; titleMessage = 'Missing meta title - add one!'; }
    else if (titleLength < 30) { titleStatus = 'warning'; titleMessage = `Too short (${titleLength} chars). Aim for 50-60 characters.`; }
    else if (titleLength > 60) { titleStatus = 'warning'; titleMessage = `Too long (${titleLength} chars). May be truncated in search results.`; }
    
    // Meta description analysis
    const descValue = metaDescription || '';
    const descLength = descValue.length;
    let descStatus: 'pass' | 'warning' | 'fail' = 'pass';
    let descMessage = 'Good length';
    if (!descValue) { descStatus = 'fail'; descMessage = 'Missing meta description - this is important for click-through rates!'; }
    else if (descLength < 120) { descStatus = 'warning'; descMessage = `Too short (${descLength} chars). Aim for 150-160 characters.`; }
    else if (descLength > 160) { descStatus = 'warning'; descMessage = `Too long (${descLength} chars). May be truncated.`; }
    
    // H1 analysis
    const h1Values = h1 ? [h1] : [];
    let h1Status: 'pass' | 'warning' | 'fail' = 'pass';
    let h1Message = 'Good - one H1 tag found';
    if (h1Values.length === 0) { h1Status = 'fail'; h1Message = 'Missing H1 tag - every page needs one main heading!'; }
    else if (h1Values.length > 1) { h1Status = 'warning'; h1Message = 'Multiple H1 tags found - use only one per page.'; }
    
    // Content analysis
    let contentStatus: 'pass' | 'warning' | 'fail' = 'pass';
    let contentMessage = 'Good content length';
    if (contentAnalysis.wordCount < 300) { contentStatus = 'fail'; contentMessage = `Only ${contentAnalysis.wordCount} words. Add more content (aim for 800+).`; }
    else if (contentAnalysis.wordCount < 800) { contentStatus = 'warning'; contentMessage = `${contentAnalysis.wordCount} words. Consider adding more (800+ recommended).`; }
    
    // Images analysis
    const imagesWithoutAlt = images.filter(img => !img.alt || img.alt.trim() === '').map(img => img.src);
    let imagesStatus: 'pass' | 'warning' | 'fail' = 'pass';
    let imagesMessage = 'All images have alt text';
    if (images.length === 0) { imagesStatus = 'warning'; imagesMessage = 'No images found. Consider adding relevant images.'; }
    else if (imagesWithoutAlt.length > 0) { imagesStatus = 'fail'; imagesMessage = `${imagesWithoutAlt.length} image(s) missing alt text.`; }
    
    // Calculate score
    let score = 100;
    if (titleStatus === 'fail') score -= 15; else if (titleStatus === 'warning') score -= 5;
    if (descStatus === 'fail') score -= 15; else if (descStatus === 'warning') score -= 5;
    if (h1Status === 'fail') score -= 15; else if (h1Status === 'warning') score -= 5;
    if (contentStatus === 'fail') score -= 20; else if (contentStatus === 'warning') score -= 10;
    if (imagesStatus === 'fail') score -= 10; else if (imagesStatus === 'warning') score -= 5;
    
    return {
      url, title, score: Math.max(0, score), lastAnalyzed: new Date().toISOString(),
      meta: {
        title: { value: titleValue, length: titleLength, status: titleStatus, message: titleMessage },
        description: { value: descValue, length: descLength, status: descStatus, message: descMessage },
        canonical: { value: url, status: 'pass', message: 'Canonical URL set' }
      },
      headings: { h1: { count: h1Values.length, values: h1Values, status: h1Status, message: h1Message }, h2: { count: h2s.length, values: h2s }, h3: { count: 0, values: [] } },
      content: { wordCount: contentAnalysis.wordCount, status: contentStatus, message: contentMessage, readabilityScore: contentAnalysis.readabilityScore, readabilityGrade: contentAnalysis.readabilityGrade },
      images: { total: images.length, withAlt: images.length - imagesWithoutAlt.length, withoutAlt: imagesWithoutAlt, status: imagesStatus, message: imagesMessage },
      links: { internal: 0, external: 0, broken: [], status: 'pass', message: 'Links look good' },
      keywords: []
    };
  }

  // Get tracked keywords
  getTrackedKeywords(): TrackedKeyword[] {
    const data = this.getStoredData();
    return data.keywords || [];
  }
  
  // Add a keyword to track
  addKeyword(keyword: string, targetPageId: string, targetPageUrl: string, targetPageTitle: string, isPrimary: boolean = false): TrackedKeyword {
    const data = this.getStoredData();
    const newKeyword: TrackedKeyword = {
      id: `kw_${Date.now()}`,
      keyword,
      targetPageId,
      targetPageUrl,
      targetPageTitle,
      isPrimary,
      createdAt: new Date().toISOString(),
      lastChecked: new Date().toISOString()
    };
    data.keywords = [...(data.keywords || []), newKeyword];
    this.saveData(data);
    return newKeyword;
  }
  
  // Remove a tracked keyword
  removeKeyword(keywordId: string): void {
    const data = this.getStoredData();
    data.keywords = (data.keywords || []).filter((k: TrackedKeyword) => k.id !== keywordId);
    this.saveData(data);
  }
  
  // Get suggested keywords
  getSuggestedKeywords(): SuggestedKeyword[] {
    return CAR_RENTAL_KEYWORDS;
  }
  
  // Analyze keyword on page
  analyzeKeyword(keyword: string, pageContent: string, pageTitle: string, pageUrl: string, h1: string): KeywordAnalysis {
    const keywordLower = keyword.toLowerCase();
    const { density, occurrences } = this.calculateKeywordDensity(pageContent, keyword);
    const first100Words = pageContent.split(/\s+/).slice(0, 100).join(' ').toLowerCase();
    
    const inTitle = pageTitle.toLowerCase().includes(keywordLower);
    const inH1 = h1.toLowerCase().includes(keywordLower);
    const inUrl = pageUrl.toLowerCase().includes(keywordLower.replace(/\s+/g, '-'));
    const inFirst100Words = first100Words.includes(keywordLower);
    
    let status: 'good' | 'needs-work' | 'missing' = 'good';
    const checks = [inTitle, inH1, inUrl, inFirst100Words, density >= 0.5 && density <= 2.5];
    const passedChecks = checks.filter(Boolean).length;
    if (passedChecks <= 1) status = 'missing';
    else if (passedChecks <= 3) status = 'needs-work';
    
    return { keyword, targetPage: pageUrl, inTitle, inH1, inUrl, inFirst100Words, density, occurrences, status };
  }

  // Get SEO score history
  getScoreHistory(): { date: string; score: number }[] {
    const data = this.getStoredData();
    return data.scoreHistory || [];
  }
  
  // Add score to history
  addScoreToHistory(score: number): void {
    const data = this.getStoredData();
    const today = new Date().toISOString().split('T')[0];
    const history = data.scoreHistory || [];
    
    // Update today's score or add new entry
    const todayIndex = history.findIndex((h: any) => h.date === today);
    if (todayIndex >= 0) {
      history[todayIndex].score = score;
    } else {
      history.push({ date: today, score });
    }
    
    // Keep last 30 days
    data.scoreHistory = history.slice(-30);
    this.saveData(data);
  }
  
  // Generate issues from page analysis
  generateIssues(analysis: PageSEOAnalysis): SEOIssue[] {
    const issues: SEOIssue[] = [];
    
    if (analysis.meta.title.status === 'fail') {
      issues.push({
        id: `issue_${Date.now()}_title`,
        type: 'error',
        category: 'meta',
        title: 'Missing Meta Title',
        description: analysis.meta.title.message,
        page: analysis.title,
        pageUrl: analysis.url,
        howToFix: 'Add a meta title tag with 50-60 characters including your main keyword.',
        impact: 'high'
      });
    } else if (analysis.meta.title.status === 'warning') {
      issues.push({
        id: `issue_${Date.now()}_title_warn`,
        type: 'warning',
        category: 'meta',
        title: 'Meta Title Length Issue',
        description: analysis.meta.title.message,
        page: analysis.title,
        pageUrl: analysis.url,
        howToFix: 'Adjust your title to be between 50-60 characters for optimal display.',
        impact: 'medium'
      });
    }
    
    if (analysis.meta.description.status === 'fail') {
      issues.push({
        id: `issue_${Date.now()}_desc`,
        type: 'error',
        category: 'meta',
        title: 'Missing Meta Description',
        description: analysis.meta.description.message,
        page: analysis.title,
        pageUrl: analysis.url,
        howToFix: 'Write a compelling 150-160 character description that includes your keyword and encourages clicks.',
        impact: 'high'
      });
    }
    
    if (analysis.headings.h1.status === 'fail') {
      issues.push({
        id: `issue_${Date.now()}_h1`,
        type: 'error',
        category: 'content',
        title: 'Missing H1 Heading',
        description: analysis.headings.h1.message,
        page: analysis.title,
        pageUrl: analysis.url,
        howToFix: 'Add one H1 tag as the main heading of your page. Include your primary keyword.',
        impact: 'high'
      });
    }
    
    if (analysis.content.status === 'fail' || analysis.content.status === 'warning') {
      issues.push({
        id: `issue_${Date.now()}_content`,
        type: analysis.content.status === 'fail' ? 'error' : 'warning',
        category: 'content',
        title: 'Content Too Short',
        description: analysis.content.message,
        page: analysis.title,
        pageUrl: analysis.url,
        howToFix: 'Add more valuable content. Aim for 800+ words on main pages and 1000+ on blog posts.',
        impact: analysis.content.status === 'fail' ? 'high' : 'medium'
      });
    }
    
    if (analysis.images.status === 'fail') {
      issues.push({
        id: `issue_${Date.now()}_images`,
        type: 'error',
        category: 'images',
        title: 'Images Missing Alt Text',
        description: analysis.images.message,
        page: analysis.title,
        pageUrl: analysis.url,
        howToFix: 'Add descriptive alt text to all images. Describe what the image shows.',
        impact: 'medium'
      });
    }
    
    return issues;
  }
  
  // Get overall SEO score
  calculateOverallScore(pageAnalyses: PageSEOAnalysis[]): SEOScore {
    if (pageAnalyses.length === 0) {
      return { overall: 0, metaTags: 0, content: 0, technical: 0, keywords: 0 };
    }
    
    const avgScore = pageAnalyses.reduce((sum, p) => sum + p.score, 0) / pageAnalyses.length;
    const metaScore = pageAnalyses.reduce((sum, p) => {
      let score = 100;
      if (p.meta.title.status === 'fail') score -= 50;
      else if (p.meta.title.status === 'warning') score -= 20;
      if (p.meta.description.status === 'fail') score -= 50;
      else if (p.meta.description.status === 'warning') score -= 20;
      return sum + Math.max(0, score);
    }, 0) / pageAnalyses.length;
    
    const contentScore = pageAnalyses.reduce((sum, p) => {
      let score = 100;
      if (p.content.status === 'fail') score -= 50;
      else if (p.content.status === 'warning') score -= 25;
      if (p.headings.h1.status === 'fail') score -= 30;
      return sum + Math.max(0, score);
    }, 0) / pageAnalyses.length;
    
    return {
      overall: Math.round(avgScore),
      metaTags: Math.round(metaScore),
      content: Math.round(contentScore),
      technical: 85, // Placeholder
      keywords: 70 // Placeholder
    };
  }
}

export const seoService = new SEOService();
